import { FCM } from "@capacitor-community/fcm";
import { PushNotifications, Token } from "@capacitor/push-notifications";
import { Capacitor } from "@capacitor/core";
import { toast } from "sonner";
import { store } from "./redux/store";
import { addNotification, markAsRead } from "./redux/slices/notificationsSlice";
import { API_BASE_URL } from "./services/api";
import { addLog } from "./logger";
import { getStoredPhoneId } from "./utils/deviceId";

/** Custom event name for deep link navigation from notification tap */
export const NOTIFICATION_ACTION_EVENT = "notification-action";

export interface NotificationActionDetail {
  path: string;
  data?: Record<string, unknown>;
}

const LAST_TOKEN_KEY = "last_registered_fcm_token";

// Set VITE_ENABLE_PUSH=false for builds without google-services.json to avoid native crash
const pushEnabled =
  (import.meta.env.VITE_ENABLE_PUSH as string | undefined) !== "false";

export const initFCM = async () => {
  const state = store.getState();
  const accessToken = state.auth?.token;

  if (!Capacitor.isNativePlatform()) {
    addLog("Push notifications: skipped (web; only supported on native Android/iOS)");
    return;
  }

  if (!pushEnabled) {
    addLog("Push notifications: disabled (VITE_ENABLE_PUSH=false or missing google-services.json)");
    return;
  }

  try {
    addLog("Push notifications: requesting permissions and registering...");

    try {
      await PushNotifications.requestPermissions();
    } catch (permError) {
      addLog(`Push permission request failed (may need google-services.json): ${permError instanceof Error ? permError.message : String(permError)}`);
      return;
    }

    try {
      await PushNotifications.register();
    } catch (regError) {
      addLog(`Push registration failed (may need google-services.json): ${regError instanceof Error ? regError.message : String(regError)}`);
      return;
    }

    PushNotifications.addListener("registration", async (token: Token) => {
      addLog(`FCM token received: ${token.value.substring(0, 20)}...`);
      const { value: lastToken } = await Preferences.get({ key: LAST_TOKEN_KEY });
      if (lastToken === token.value) {
        addLog("Token unchanged; skipping backend register (avoids duplicate key error)");
        try {
          await FCM.subscribeTo({ topic: "news" });
          addLog("Subscribed to FCM topic: news");
        } catch (e) {
          addLog(`FCM topic subscribe: ${e instanceof Error ? e.message : String(e)}`);
        }
        return;
      }
      const phoneId = await getStoredPhoneId();
      const url = `${API_BASE_URL.replace(/\/$/, "")}/mobile-notifications/token/register`;
      addLog(`Registering token with backend: ${url}`);

      const currentToken = store.getState().auth?.token;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (currentToken) {
        headers.Authorization = `Bearer ${currentToken}`;
      } else {
        addLog("No auth token; registering as anonymous (backend must accept device/phone_id).");
      }

      try {
        const res = await fetch(url, {
          method: "POST",
          headers,
          body: JSON.stringify({
            token: token.value,
            platform: Capacitor.getPlatform() === "ios" ? "ios" : "android",
            app_version: "1.0.0",
            phone_id: phoneId,
          }),
        });

        if (!res.ok) {
          const text = await res.text();
          addLog(`Token registration failed (${res.status}): ${text.substring(0, 150)}`);
        } else {
          await Preferences.set({ key: LAST_TOKEN_KEY, value: token.value });
          addLog("Token successfully registered with backend");
        }
      } catch (error) {
        addLog(`Token registration error: ${error instanceof Error ? error.message : String(error)}`);
      }

      try {
        await FCM.subscribeTo({ topic: "news" });
        addLog("Subscribed to FCM topic: news");
      } catch (e) {
        addLog(`FCM topic subscribe: ${e instanceof Error ? e.message : String(e)}`);
      }
    });

    PushNotifications.addListener("registrationError", (err: unknown) => {
      addLog(`FCM registration error: ${err instanceof Error ? err.message : String(err)}`);
    });

    PushNotifications.addListener("pushNotificationReceived", (notification) => {
      const title = notification.title ?? "New Notification";
      const body = notification.body ?? "";
      const data = (notification.data as Record<string, unknown>) ?? {};
      const id = `push-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      addLog(`Push received: ${title} - ${body}`);

      store.dispatch(
        addNotification({
          id,
          title,
          body,
          data,
          timestamp: Date.now(),
        })
      );

      toast(title, {
        description: body,
        action: {
          label: "View",
          onClick: () => handleNotificationAction(data),
        },
      });
    });

    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const notification = action.notification;
      const id = notification.id ?? `tap-${Date.now()}`;
      addLog(`Push action: ${action.actionId ?? "tap"}`);
      store.dispatch(markAsRead(id));
      const data = (notification.data as Record<string, unknown>) ?? {};
      handleNotificationAction(data);
    });
  } catch (err) {
    addLog(`FCM init error: ${err instanceof Error ? err.message : String(err)}`);
  }
};

function handleNotificationAction(data: Record<string, unknown>) {
  const caseId = data?.case_id ?? data?.entity_id;
  if (caseId != null) {
    const path = `/case/${caseId}`;
    window.dispatchEvent(
      new CustomEvent<NotificationActionDetail>(NOTIFICATION_ACTION_EVENT, {
        detail: { path, data },
      })
    );
  }
}
